import { reflect } from '@effector/reflect';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { $t, breakpoints } from 'shared/lib';
import { ConfirmModalProps, createConfirmModal } from 'shared/ui';
import ResetBalance from 'shared/ui/assets/reset-balance-icon.svg?react';
import { ResetPlayerBalanceModel } from '../../model';

export const createResetPlayerBalance = (model: ResetPlayerBalanceModel) => {
	const ConfirmModal = createConfirmModal(model);

	const ResetPlayerBalanceModal = forwardRef<HTMLDivElement, ConfirmModalProps>(
		({ ...props }, ref) => {
			const { t } = useTranslation();

			const [md, player] = useUnit([breakpoints.md, model.$modalData]);

			return (
				<ConfirmModal
					ref={ref}
					title={t('reset-player-balance.title')}
					submitButtonProps={{
						children: t('reset-player-balance.reset'),
						leftSection: <ResetBalance />,
						variant: 'primary',
						size: md ? 'xl' : 'lg',
						'data-test-id': 'reset-player-balance-submit',
						fullWidth: true,
					}}
					variant='default'
					size={md ? '2xl' : 'xl'}
					{...props}
				>
					{t('reset-player-balance.description', { name: player?.name ?? '' })}
				</ConfirmModal>
			);
		},
	);

	const ResetPlayerBalanceButton = reflect({
		view: ConfirmModal.Button,
		bind: {
			size: breakpoints.md.map((md) => (md ? 'lg' : 'md')),
			leftSection: breakpoints.sm.map((sm) => (sm ? null : <ResetBalance />)),
			children: combine([breakpoints.md, breakpoints.sm, $t]).map(([md, sm, t]) =>
				sm ? (
					<ResetBalance
						width={md ? 24 : 16}
						height={md ? 24 : 16}
					/>
				) : (
					t('reset-player-balance.reset')
				),
			),
			'data-test-id': 'reset-player-balance-button',
		},
	});

	return Object.assign(ResetPlayerBalanceModal, {
		Button: ResetPlayerBalanceButton,
	});
};
